import { useState, useEffect, useContext, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
    Grid,
    Box,
    Typography,
    Chip,
    Paper,
    Stack,
    Divider,
    alpha,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import DescriptionIcon from '@mui/icons-material/Description';
import ImageIcon from '@mui/icons-material/Image';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import TableChartIcon from '@mui/icons-material/TableChart';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PublicIcon from '@mui/icons-material/Public';
import LockIcon from '@mui/icons-material/Lock';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { sporttiaColors } from '../../theme/theme';
import {
    AppContext,
    SttDialog,
    SttFormText,
    SttFormTextarea,
    SttFormCheck,
} from '../all';
import { getPath } from '../../pages/Pages';
import { useInteractionsFiles } from '../../lib/hooks';
import DateBuilder from '../../lib/DateBuilder';

type FileData = {
    id: number;
    name: string;
    description?: string;
    format?: string;
    public?: boolean;
    isImage?: boolean;
    url?: string;
    created?: string;
    size?: number;
};

type FileApiResponse = {
    file: FileData;
};

type Props = {
    idFile?: number | null;
    onClose?: () => void;
    onUpdate?: (file: FileData) => void;
};

type DialogButton = {
    show?: boolean;
    caption?: string;
    icon?: React.ReactNode;
    onClick?: () => void;
    type?: 'delete' | 'save';
};

type FileTypeConfig = {
    icon: React.ReactElement;
    color: string;
    bgColor: string;
    label: string;
};

export function SttFileDialog({
    idFile,
    onClose,
    onUpdate,
}: Props): React.ReactElement {
    const cxt = useContext(AppContext)!;
    const { downloadFile } = useInteractionsFiles();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const [file, setFile] = useState<FileData | null>(null);

    const loadFile = useCallback(() => {
        if (!idFile) return;
        cxt?.api('GET', `/files/${idFile}`, {
            success: (r: FileApiResponse) => {
                setFile(r.file);
            },
        });
    }, [cxt, idFile]);

    const saveFile = useCallback(() => {
        if (!file) return;

        cxt?.api('PUT', `/files/${file.id}`, {
            params: file,
            success: (r: FileApiResponse) => {
                setFile(r.file);
                cxt?.showMessage('S', t('messages.saved'));
                onUpdate?.(r.file);
            },
        });
    }, [cxt, file, onUpdate, t]);

    const delFile = useCallback(() => {
        if (!file) return;

        cxt?.api('DELETE', `/files/${file.id}`, {
            confirmation: true,
            success: (r: FileApiResponse) => {
                cxt?.showMessage('S', t('messages.deleted'));
                onClose?.();
                onUpdate?.(r.file);
            },
        });
    }, [cxt, file, onClose, onUpdate, t]);

    useEffect(() => {
        if (idFile) {
            loadFile();
        } else {
            setFile(null);
        }
    }, [idFile, loadFile]);

    const download = useCallback(() => {
        if (!file) return;
        downloadFile(`/files/${file.id}.pdf`, 'application/pdf', undefined);
    }, [downloadFile, file]);

    const downloadCSV = useCallback(() => {
        if (!file) return;
        downloadFile(`/files/${file.id}.csv`, 'text/csv', undefined);
    }, [downloadFile, file]);

    const seeFileCSV = useCallback(() => {
        if (!file) return;
        navigate(getPath('fileCSV', { id: file.id }));
    }, [file, navigate]);

    const handleForm = useCallback(
        ({ name, value }: { name: string | undefined; value: unknown }) => {
            if (!name || !file) return;
            setFile({ ...file, [name]: value } as FileData);
        },
        [file],
    );

    const openImageInNewTab = useCallback(() => {
        if (file?.url) {
            window.open(file.url, '_blank');
        }
    }, [file?.url]);

    const getFileTypeConfig = useCallback((): FileTypeConfig => {
        if (!file) {
            return {
                icon: <InsertDriveFileIcon sx={{ fontSize: 32 }} />,
                color: '#757575',
                bgColor: '#f5f5f5',
                label: t('file.document'),
            };
        }

        const format = file.format?.toLowerCase() || '';

        if (file.isImage) {
            return {
                icon: <ImageIcon sx={{ fontSize: 32 }} />,
                color: 'var(--brand-text)',
                bgColor: alpha(sporttiaColors.primary.main, 0.1),
                label: t('file.image'),
            };
        }
        if (format.includes('pdf')) {
            return {
                icon: <PictureAsPdfIcon sx={{ fontSize: 32 }} />,
                color: '#e53935',
                bgColor: alpha('#e53935', 0.1),
                label: 'PDF',
            };
        }
        if (
            format.includes('csv') ||
            format.includes('excel') ||
            format.includes('spreadsheet')
        ) {
            return {
                icon: <TableChartIcon sx={{ fontSize: 32 }} />,
                color: '#43a047',
                bgColor: alpha('#43a047', 0.1),
                label: format.includes('csv') ? 'CSV' : 'Excel',
            };
        }
        return {
            icon: <DescriptionIcon sx={{ fontSize: 32 }} />,
            color: '#1976d2',
            bgColor: alpha('#1976d2', 0.1),
            label: file.format || t('file.document'),
        };
    }, [file, t]);

    const formatFileSize = useCallback((bytes?: number): string => {
        if (!bytes) return '';
        if (bytes < 1024) return `${bytes} B`;
        if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
        return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    }, []);

    const isSportcenter = cxt?.logged && cxt?.user?.role === 'SPORTCENTER';
    const isCSV = file?.format?.toLowerCase() === 'text/csv';
    const isPDF = file?.format?.toLowerCase() === 'application/pdf';
    const fileTypeConfig = getFileTypeConfig();

    // Use larger dialog for images to show side-by-side layout
    const dialogMaxWidth = file?.isImage ? 'lg' : 'md';

    const buttons: DialogButton[] = [
        {
            show: !!(file && isCSV),
            caption: t('general.see'),
            icon: <VisibilityIcon />,
            onClick: seeFileCSV,
        },
        {
            show: !!(file && isCSV),
            caption: t('general.download'),
            icon: <CloudDownloadIcon />,
            onClick: downloadCSV,
        },
        {
            show: !!(file && isPDF),
            caption: t('general.download'),
            icon: <CloudDownloadIcon />,
            onClick: download,
        },
        {
            show: !!isSportcenter,
            type: 'delete',
            onClick: delFile,
        },
        {
            show: !!isSportcenter,
            type: 'save',
            onClick: saveFile,
        },
    ];

    // Reusable details panel for both layouts
    const renderDetailsPanel = () => (
        <Box>
            <Typography
                variant="subtitle2"
                color="text.secondary"
                sx={{ mb: 2, fontWeight: 600 }}
            >
                {t('labels.details')}
            </Typography>
            <Grid container spacing={2}>
                <SttFormText
                    grid
                    md={12}
                    name="name"
                    caption={t('labels.name')}
                    defVal={file?.name}
                    onChange={handleForm}
                    disabled={!isSportcenter}
                />

                <SttFormTextarea
                    grid
                    md={12}
                    name="description"
                    caption={t('labels.description')}
                    defVal={file?.description}
                    onChange={handleForm}
                    rows={3}
                    disabled={!isSportcenter}
                />

                {isSportcenter && (
                    <Grid size={{ xs: 12 }}>
                        <Paper
                            elevation={0}
                            sx={{
                                p: 2,
                                backgroundColor: alpha(
                                    sporttiaColors.primary.main,
                                    0.05,
                                ),
                                borderRadius: 2,
                                border: '1px solid',
                                borderColor: alpha(
                                    sporttiaColors.primary.main,
                                    0.2,
                                ),
                            }}
                        >
                            <SttFormCheck
                                name="public"
                                caption={t('file.makePublic')}
                                checked={!!file?.public}
                                onChange={handleForm}
                            />
                            <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{
                                    display: 'block',
                                    ml: 4,
                                    mt: 0.5,
                                }}
                            >
                                {t('file.publicDescription')}
                            </Typography>
                        </Paper>
                    </Grid>
                )}
            </Grid>
        </Box>
    );

    // Render image content with side-by-side layout
    const renderImageContent = () => (
        <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={3}
            alignItems="flex-start"
        >
            {/* Left: Image Preview */}
            <Box sx={{ flex: '1 1 60%', minWidth: 0 }}>
                <Paper
                    elevation={0}
                    sx={{
                        borderRadius: 2,
                        overflow: 'hidden',
                        border: '1px solid',
                        borderColor: 'divider',
                        position: 'relative',
                        cursor: 'pointer',
                        '&:hover .image-overlay': {
                            opacity: 1,
                        },
                    }}
                    onClick={openImageInNewTab}
                >
                    <Box
                        component="img"
                        src={file?.url}
                        alt={file?.name}
                        sx={{
                            width: '100%',
                            maxHeight: 500,
                            objectFit: 'contain',
                            backgroundColor: '#f5f5f5',
                            display: 'block',
                        }}
                    />
                    <Box
                        className="image-overlay"
                        sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            backgroundColor: alpha('#000', 0.4),
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            opacity: 0,
                            transition: 'opacity 0.2s ease',
                        }}
                    >
                        <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                            sx={{ color: 'white' }}
                        >
                            <OpenInNewIcon />
                            <Typography variant="body2" fontWeight={500}>
                                {t('general.openInNewTab')}
                            </Typography>
                        </Stack>
                    </Box>
                </Paper>
            </Box>

            {/* Right: Details Panel */}
            <Box sx={{ flex: '1 1 40%', minWidth: { xs: 'auto', md: 300 } }}>
                {/* File metadata */}
                <Paper
                    elevation={0}
                    sx={{
                        p: 2,
                        mb: 2,
                        backgroundColor: '#fafafa',
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: 'divider',
                    }}
                >
                    <Stack spacing={1.5}>
                        {file?.id && (
                            <Typography variant="body2" color="text.secondary">
                                <strong>ID:</strong> {file.id}
                            </Typography>
                        )}
                        <Stack
                            direction="row"
                            spacing={1}
                            flexWrap="wrap"
                            useFlexGap
                        >
                            <Chip
                                label={fileTypeConfig.label}
                                size="small"
                                sx={{
                                    fontWeight: 600,
                                    backgroundColor: fileTypeConfig.bgColor,
                                    color: fileTypeConfig.color,
                                }}
                            />
                            <Chip
                                icon={
                                    file?.public ? (
                                        <PublicIcon
                                            sx={{ fontSize: '16px !important' }}
                                        />
                                    ) : (
                                        <LockIcon
                                            sx={{ fontSize: '16px !important' }}
                                        />
                                    )
                                }
                                label={
                                    file?.public
                                        ? t('file.public')
                                        : t('file.private')
                                }
                                size="small"
                                color={file?.public ? 'success' : 'default'}
                                variant="outlined"
                            />
                        </Stack>
                        {file?.created && (
                            <Stack
                                direction="row"
                                spacing={0.5}
                                alignItems="center"
                            >
                                <AccessTimeIcon
                                    sx={{
                                        fontSize: 16,
                                        color: 'text.secondary',
                                    }}
                                />
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                >
                                    {new DateBuilder(
                                        file.created,
                                        cxt?.sc?.timezone?.name,
                                        cxt?.lang?.key,
                                    ).dmy()}
                                </Typography>
                            </Stack>
                        )}
                        {file?.size && (
                            <Typography variant="body2" color="text.secondary">
                                {formatFileSize(file.size)}
                            </Typography>
                        )}
                    </Stack>
                </Paper>

                {/* Details form */}
                {renderDetailsPanel()}
            </Box>
        </Stack>
    );

    // Render non-image content (vertical layout)
    const renderNonImageContent = () => (
        <Stack spacing={3}>
            {/* File Header Card */}
            <Paper
                elevation={0}
                sx={{
                    p: 2.5,
                    backgroundColor: '#fafafa',
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                }}
            >
                <Stack direction="row" spacing={2.5} alignItems="center">
                    {/* File Icon */}
                    <Box
                        sx={{
                            width: 64,
                            height: 64,
                            borderRadius: 2,
                            backgroundColor: fileTypeConfig.bgColor,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: fileTypeConfig.color,
                            flexShrink: 0,
                        }}
                    >
                        {fileTypeConfig.icon}
                    </Box>

                    {/* File Info */}
                    <Box flex={1} minWidth={0}>
                        <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                            flexWrap="wrap"
                            useFlexGap
                        >
                            <Chip
                                label={fileTypeConfig.label}
                                size="small"
                                sx={{
                                    fontWeight: 600,
                                    backgroundColor: fileTypeConfig.bgColor,
                                    color: fileTypeConfig.color,
                                }}
                            />
                            <Chip
                                icon={
                                    file?.public ? (
                                        <PublicIcon
                                            sx={{ fontSize: '16px !important' }}
                                        />
                                    ) : (
                                        <LockIcon
                                            sx={{ fontSize: '16px !important' }}
                                        />
                                    )
                                }
                                label={
                                    file?.public
                                        ? t('file.public')
                                        : t('file.private')
                                }
                                size="small"
                                color={file?.public ? 'success' : 'default'}
                                variant="outlined"
                            />
                        </Stack>

                        {/* Metadata row */}
                        <Stack
                            direction="row"
                            spacing={2}
                            mt={1.5}
                            divider={
                                <Divider orientation="vertical" flexItem />
                            }
                        >
                            {file?.id && (
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                >
                                    <strong>ID:</strong> {file.id}
                                </Typography>
                            )}
                            {file?.created && (
                                <Stack
                                    direction="row"
                                    spacing={0.5}
                                    alignItems="center"
                                >
                                    <AccessTimeIcon
                                        sx={{
                                            fontSize: 16,
                                            color: 'text.secondary',
                                        }}
                                    />
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                    >
                                        {new DateBuilder(
                                            file.created,
                                            cxt?.sc?.timezone?.name,
                                            cxt?.lang?.key,
                                        ).dmy()}
                                    </Typography>
                                </Stack>
                            )}
                            {file?.size && (
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                >
                                    {formatFileSize(file.size)}
                                </Typography>
                            )}
                        </Stack>
                    </Box>
                </Stack>
            </Paper>

            {/* Form Fields */}
            {renderDetailsPanel()}
        </Stack>
    );

    return (
        <SttDialog
            title={file?.name || t('file.file')}
            open={!!idFile}
            onClose={() => onClose?.()}
            maxWidth={dialogMaxWidth}
            content={
                <>
                    {file && (
                        <>
                            {file.isImage && file.url
                                ? renderImageContent()
                                : renderNonImageContent()}
                        </>
                    )}
                </>
            }
            buttons={
                buttons as React.ComponentProps<typeof SttDialog>['buttons']
            }
        />
    );
}
